import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PROVIDERS } from "@/lib/providers";
import { LOCATIE_TEKST, locatieVan } from "@/lib/aanbieder-locatie";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { SOORTEN, STANDAARD_POORT, adresKeuze, adresLabel } from "@/lib/code-model-adres";

/**
 * LC1 (GO Frank 30 oktober 2026): het model voor code, als losse keuze.
 *
 * Dezelfde keuze staat in Instellingen en in het scherm Code; beide schrijven `code_model` en niets
 * anders, zodat de knop Opslaan van Instellingen haar niet terugzet. De lijst komt van de server en
 * bevat alleen modellen die Ollama meldt als tekst en gereedschap; leeg is hetzelfde als de chat.
 */
export const ZELFDE = "__zelfde_als_chat__";

/**
 * GL222-5 (glasronde 0.9.222): de keuzes in deze lijst. Eerst de lokale modellen die tekst en gereedschap kunnen,
 * dan elk cloudmodel van een aanbieder waarvoor een sleutel is ingevuld, als `aanbieder|model`, met de plek erbij
 * (aanbieder-locatie.js, "Buiten de EU"). De keuze geldt alleen voor Code; de aanbieder van de chat verandert niet. PUUR.
 */
export function codeModelOpties(lokaal = [], sleutels = []) {
  // Een lokaal model draagt geen plek in de lijst: zo stond hij er al. Een cloudmodel wel.
  const uit = lokaal.map((m) => ({ value: m, label: m, plek: "" }));
  for (const aanbieder of sleutels) {
    const p = PROVIDERS[aanbieder];
    if (!p || aanbieder === "ollama") continue;
    for (const m of p.models || []) {
      uit.push({ value: `${aanbieder}|${m.value}`, label: `${p.name}: ${m.label}`, plek: locatieVan(aanbieder) });
    }
  }
  return uit;
}

export default function CodeModelKeuze({ compact = false }) {
  const { t } = useTaal();
  const [modellen, setModellen] = useState([]);
  const [sleutels, setSleutels] = useState([]);
  const [gekozen, setGekozen] = useState("");
  const [geladen, setGeladen] = useState(false);
  // Qwen-klaar (3 nov): een model op een andere machine, Ollama of LM Studio, via het netwerkadres van die machine.
  const [soort, setSoort] = useState("ollama");
  const [adres, setAdres] = useState("");
  const [model, setModel] = useState("");

  const laad = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ollama/code-models`);
      setModellen(data.models || []);
      setSleutels(data.sleutels || []);
      setGekozen(data.gekozen || "");
    } catch {
      setModellen([]);
    } finally {
      setGeladen(true);
    }
  }, []);

  useEffect(() => { laad(); }, [laad]);

  async function kies(waarde) {
    const naam = waarde === ZELFDE ? "" : waarde;
    const vorige = gekozen;
    setGekozen(naam);
    try {
      await axios.put(`${API}/settings`, { code_model: naam });
      return true;
    } catch (error) {
      setGekozen(vorige);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? t(detail) : t("Het model voor code is niet opgeslagen"));
      return false;
    }
  }

  // Een gekozen model dat niet meer in de lijst staat (verwijderd uit Ollama, of een sleutel die weg is) blijft zichtbaar.
  const alle = codeModelOpties(modellen, sleutels);
  const opties = gekozen && !alle.some((o) => o.value === gekozen)
    ? [{ value: gekozen, label: adresLabel(gekozen, t) || gekozen, plek: "" }, ...alle] : alle;
  const nieuw = adresKeuze(soort, adres, model);
  return (
    <div className={compact ? "flex items-center gap-2" : "space-y-2"} data-testid="code-model-keuze">
      <Label htmlFor="code-model" className={compact ? "text-xs text-muted-foreground shrink-0" : undefined}>
        {t("Model voor code")}
      </Label>
      <Select value={gekozen || ZELFDE} onValueChange={kies} disabled={!geladen}>
        <SelectTrigger id="code-model" className={compact ? "h-8 text-xs" : undefined} data-testid="code-model-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ZELFDE}>{t("Zelfde als de chat")}</SelectItem>
          {opties.map((o) => (
            <SelectItem key={o.value} value={o.value} data-testid={`code-model-optie-${o.value}`}>
              {o.label}{o.plek ? ` (${t(LOCATIE_TEKST[o.plek])})` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!compact && geladen && alle.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="code-model-leeg">
          {t("Geen lokaal model dat tekst en gereedschap kan, en geen cloudmodel met een sleutel. Code gebruikt het model van de chat.")}
        </p>
      )}
      {!compact && (
        <p className="text-xs text-muted-foreground">
          {t("Lokale modellen die tekst en gereedschap kunnen, en de cloudmodellen waarvoor een sleutel is ingevuld. De keuze geldt alleen voor Code; de chat houdt haar eigen model.")}
        </p>
      )}
      {!compact && (
        <form className="space-y-2 rounded-md border p-2" data-testid="code-model-adres"
              onSubmit={async (e) => {
                e.preventDefault();
                if (nieuw && await kies(nieuw)) { setAdres(""); setModel(""); }
              }}>
          <p className="text-xs font-medium">{t("Of een model op een andere machine in je netwerk")}</p>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex rounded-md border" role="group" aria-label={t("Soort")}>
              {Object.entries(SOORTEN).map(([k, naam]) => (
                <button key={k} type="button" aria-pressed={soort === k} onClick={() => setSoort(k)}
                        className={`px-2 text-sm ${soort === k ? "bg-muted font-medium" : ""}`}
                        data-testid={`code-model-soort-${k}`}>{naam}</button>
              ))}
            </span>
            <Input value={adres} onChange={(e) => setAdres(e.target.value)} className="h-8 min-w-0 flex-1 text-sm"
                   placeholder={`http://192.168.1.20:${STANDAARD_POORT[soort]}`} aria-label={t("Adres van die machine")}
                   data-testid="code-model-adres-veld" />
            <Input value={model} onChange={(e) => setModel(e.target.value)} className="h-8 min-w-0 flex-1 text-sm"
                   placeholder="qwen2.5-coder:14b" aria-label={t("Naam van het model")} data-testid="code-model-naam-veld" />
            <Button type="submit" size="sm" disabled={!nieuw} data-testid="code-model-adres-opslaan">
              {t("Gebruik voor Code")}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("Alleen een machine in je eigen netwerk. NovaMund controleert bij het opslaan of het model daar code kan schrijven; er wordt niets gedownload.")}
          </p>
        </form>
      )}
    </div>
  );
}
